import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PowerOff } from 'lucide-react';
import { ActiveConnection } from '../../connections/models/connectionTypes';
import { Button } from '../../../components/ui/button';
import { DatabaseExplorerTree } from './DatabaseExplorerTree';
import { useDatabaseStore } from '../store/databaseStore';
import { useDatabaseUIStore } from '../store/uiStore';
import { useSidebarStore } from '../store/sidebarStore';
import { processDatabaseTree } from '../services/databaseNodeService';
import { buildDatabaseTree, mergeConnectionsIntoTree } from '../services/databaseTreeBuilder';
import { filterTreeNodes } from '../services/treeFilterService';
import { findNodeById } from '../../explorer/services/treeService';
import {
  ConnectionTreeNode,
  DatabaseExplorerNode,
  DatabaseObjectTreeNode,
  DatabaseTreeNode,
  FolderTreeNode,
  PartitionTreeNode,
  PartitionsFolderNode,
} from '../../explorer/models/DatabaseTreeModels';
import { loadDatabasesFolder } from '../services/nodeExpansionService';
import { preloadObjectsForConnection } from '../services/objectPreloadService';
import type { ExplorerFetchOrigin } from '../services/explorerQueryUtils';
import {
  getPreloadConcurrency,
  planPreloadOrder,
  resolveHomePreloadDatabase,
} from '../services/preloadPriority';
import { createConnectionWaveQueue } from '../services/connectionWaveQueue';
import { createConnectionGenerations } from '../services/connectionGenerations';
import { evictConnectionObjectQueries } from '../services/connectionListingLifecycle';
import {
  forgetExplorerConnectLatency,
  forgetRemovedConnections,
  trackExplorerDatabasesLoaded,
} from '../services/explorerConnectTelemetry';
import { useDatabaseFetch } from '../hooks/useDatabaseFetch';
import { useNodeExpansion } from '../hooks/useNodeExpansion';
import { useNodeRefresh } from '../hooks/useNodeRefresh';
import { useSessionStore } from '../../sessions/store/sessionStore';
import { executeTableContextMenuAction, buildTableAiTaskContext } from '../services/tableContextMenuService';
import { parseAiTaskActionId } from '../services/aiSubmenuBuilder';
import { executeColumnContextMenuAction } from '../services/columnContextMenuService';
import { executeViewContextMenuAction, buildViewAiTaskContext } from '../services/viewContextMenuService';
import { useAiTaskLauncher } from '../../ai-chat/hooks/useAiTaskLauncher';
import { AiChatCliRequiredDialog } from '../../ai-chat/components/AiChatCliRequiredDialog';
import { executeRoutineContextMenuAction } from '../services/routineContextMenuService';
import { executeDatabaseContextMenuAction } from '../services/databaseContextMenuService';
import { executeConnectionContextMenuAction } from '../services/connectionContextMenuService';
import { executeSecurityContextMenuAction, isSecurityObjectType } from '../services/securityContextMenuService';
import { executeKeyContextMenuAction } from '../services/keyContextMenuService';
import { executeConstraintContextMenuAction } from '../services/constraintContextMenuService';
import { executeIndexContextMenuAction } from '../services/indexContextMenuService';
import { executeTriggerContextMenuAction } from '../services/triggerContextMenuService';
import { executeStatisticContextMenuAction } from '../services/statisticContextMenuService';
import { executeOracleObjectContextMenuAction } from '../services/oracleObjectContextMenuService';
import {
  isUserTypeObjectType,
  executeUserTypeContextMenuAction,
} from '../services/userTypeContextMenuService';
import { executePartitionContextMenuAction } from '../services/partitionContextMenuService';
import { ExplorerConfirmDialog } from './ExplorerConfirmDialog';
import { isAgentNodeType, executeAgentContextMenuAction } from '../services/agentContextMenuRouter';
import { executeDirectoryContextMenuAction } from '../services/directoryContextMenuService';
import {
  handleScriptSchemaAs,
  handleScriptAllObjects,
} from '../services/schemaContextMenuService';
import {
  handleScriptNewTable,
  handleScriptNewView,
  handleScriptNewProcedure,
  handleScriptNewFunction,
  handleScriptNewDatabase,
  handleScriptNewPackage,
  handleScriptNewSequence,
  handleScriptNewSynonym,
  handleScriptNewTrigger,
  handleScriptNewDbLink,
  createContextFromFolderMetadata,
  createContextFromDatabaseNode,
} from '../services/newObjectScriptService';
import { createDatabaseNodeId, createDatabasesFolderNodeId } from '../../explorer/utils/nodeIdUtils';
import { findTablesFolderNodeIds } from '../services/findTablesFolderNodes';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { isConnectionLostError } from '../../../core/engines/engineCapabilities';
import { confirmConnectionLost } from '../../connections/services/confirmConnectionLost';
import type { DatabaseEngineId } from '../../../core/engines/engineTypes';
import type { SecurityObjectNodeMetadata, KeyNodeMetadata, ConstraintNodeMetadata, IndexNodeMetadata, TriggerNodeMetadata, StatisticNodeMetadata } from '../../explorer/models/nodeTypes';
import {
  openSecurityCreateFromExplorerFolder,
  openSecurityManagerFromExplorerFolder,
  openSecurityPropertiesFromExplorerNode,
  openSecurityScriptAsFromExplorerNode,
} from '../services/securityObjectActionsService';
import { executeFolderContextMenuAction } from '../services/folderContextMenuService';
import { useTerminalStore } from '../../terminal/store/terminalStore';
import { createTerminalSession } from '../../terminal/services/terminalApi';
import { useAiWorkspaceSetupConsent } from '../../ai-toolbar/hooks/useAiWorkspaceSetupConsent';
import { AiWorkspaceSetupConfirmDialog } from '../../ai-toolbar/components/AiWorkspaceSetupConfirmDialog';
import { toast } from '../../../components/ui/toaster';
import type { TerminalSession } from '../../../types/terminal';
import { ExportDialog } from '../../backup-restore/components/ExportDialog';
import { ImportDialog } from '../../backup-restore/components/ImportDialog';
import { BackupDatabaseDialog } from '../../backup-restore/components/BackupDatabaseDialog';
import { RestoreDatabaseDialog } from '../../backup-restore/components/RestoreDatabaseDialog';
import { PostgresBackupDialog } from '../../backup-restore/components/PostgresBackupDialog';
import { PostgresRestoreDialog } from '../../backup-restore/components/PostgresRestoreDialog';
import { CloneDatabaseDialog } from '../../clone-database/components/CloneDatabaseDialog';
import { CloneTableDialog, CloneTableProgressDialog } from '../../clone-table';
import { useConnectionInfoDialogStore } from '../../connections/store/connectionInfoDialogStore';
import { ConnectionInfoDialog } from '../../connections/components/ConnectionInfoDialog';
import { NewConnectionPopup } from '../../connections/components/NewConnectionPopup';
import { CreateSequenceDialog } from '../../workspace/oracle-object-detail/components/CreateSequenceDialog';
import { CreateSynonymDialog } from '../../workspace/oracle-object-detail/components/CreateSynonymDialog';
import { CreateDbLinkDialog } from '../../workspace/oracle-object-detail/components/CreateDbLinkDialog';
import { RoutinePickerDialog } from '../../navigational-features/go-to/RoutinePickerDialog';
import { RenamePreviewDialog } from '../../navigational-features/rename';
import { useJobSchedulerEngineSupportVersion } from '../../workspace/job-scheduler/engineConfig';
import { useAppStore } from '../../../store/appStore';
import { applySchemaGroupingToDatabaseNode } from '../../explorer/services/schemaGroupingRestructure';
import { shouldGroupBySchema } from '../../explorer/services/schemaGrouping';
import { isCurrentlyGrouped, shouldKeepCurrentLayout } from '../services/preloadTreeLayoutGuard';
import { isDatabasesFolderUnloaded, isSameChildList } from '../services/databaseTreeBuilder';
import { showConnectionDiagnostics } from '../../connection-diagnostics/showConnectionDiagnostics';
import type { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';
import { useObjectExplorerRefreshStore } from '../store/objectExplorerRefreshStore';
import { usePinnedObjectsStore } from '../store/pinnedObjectsStore';
import { buildPinnedChildren } from '../services/pinnedObjects/pinnedChildrenBuilder';
import { preservePinnedExpansionState } from '../services/pinnedObjects/preservePinnedExpansion';
import {
  executePinnedWorkspaceContextMenuAction,
  renameWorkspacePin,
} from '../services/pinnedObjects/pinnedWorkspaceContextMenuService';
import { startSourcePinReconciler } from '../services/pinnedObjects/sourcePinReconciler';
import { RenamePinDialog } from '../../explorer/components/TreeNode/RenamePinDialog';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { isStorageNodeType } from '../../explorer/models/azureStorageNodeFactories';
import { isStorageListingQueryKey } from '../services/azureStorageListing';
import { executeStorageContextMenuAction, type StorageMenuNode } from '../services/storageContextMenuService';
import { useStorageAccountInfoSync } from '../hooks/useStorageAccountInfoSync';
import { openCreateQueueDialog } from '../../workspace/storage-explorer/messages/queueDialogs';
import { useQueryClient } from '@tanstack/react-query';
import type { Connection, PinnedObjectRef, PinnedWorkspaceRef, SaveConnectionOptions } from '../../../types/electron';
import type { DashboardsRootTreeNode, PinnedWorkspaceNode } from '../../explorer/models/nodeTypes';
import { useDashboardStore } from '../../workspace/dashboards/store/dashboardStore';
import type { Dashboard } from '../../workspace/dashboards/types/dashboardTypes';
import { DashboardSettingsDialog } from '../../workspace/dashboards/components/DashboardSettingsDialog';
import { buildDashboardsRootNode } from '../../workspace/dashboards/explorer/dashboardExplorerNode';
import { useBetaFeature } from '../../../core/beta';
import {
  openDashboardFromExplorer,
  presentDashboardFromExplorer,
  renameDashboardFromExplorer,
  saveDashboardParametersFromExplorer,
  saveDashboardPlaylistFromExplorer,
  duplicateDashboardFromExplorer,
  exportDashboardDefinitionFromExplorer,
  deleteDashboardFromExplorer,
} from '../../workspace/dashboards/explorer/dashboardExplorerActions';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

/**
 * Review finding #1 + #15 — unwrap a `pinned-database` / `pinned-folder` /
 * `pinned-object` wrapper into a synthetic CANONICAL node that the existing
 * action executors can handle without changes. Returns `null` for non-pinned
 * nodes (caller falls back to the original node).
 *
 * The synthesised node carries the exact metadata shape each executor reads:
 * - table / view / stored-procedure / function → `metadata.objectDetails` with
 *   `name`, `schema`, `connectionId`, `metadata.database` (matches
 *   `buildActionContext` in `tableContextMenuService.ts:262-292` and friends).
 * - database → top-level `metadata.{connectionId, engine}` plus `node.name`
 *   set to the database name (matches the `node.type === 'database'` branch).
 * - folder → top-level `metadata.{connectionId, database, engine}` (matches
 *   the `node.type.endsWith('-folder')` branch).
 *
 * `parentId` is intentionally `null` so the table/view/routine handlers fall
 * back to `objectDetails.metadata.database` for the database name (the
 * `extractDatabaseFromParentId` helper returns `undefined` for `null`).
 */
function unwrapPinnedForAction(
  node: DatabaseExplorerNode,
): DatabaseExplorerNode | null {
  if (
    node.type !== 'pinned-database' &&
    node.type !== 'pinned-schema' &&
    node.type !== 'pinned-folder' &&
    node.type !== 'pinned-object'
  ) {
    return null;
  }
  const pinRef = (node as { pinRef?: PinnedObjectRef }).pinRef;
  const meta = node.metadata as
    { connectionId?: string; engine?: string; database?: string }
    | undefined;
  const connectionId = meta?.connectionId;
  const engine = meta?.engine;
  if (!pinRef || !connectionId) return null;

  if (pinRef.kind === 'database') {
    return {
      ...node,
      id: node.id,
      type: 'database',
      name: pinRef.database,
      parentId: null,
      metadata: { connectionId, engine, name: pinRef.database, database: pinRef.database },
    } as unknown as DatabaseExplorerNode;
  }

  if (pinRef.kind === 'schema') {
    return {
      ...node,
      id: node.id,
      type: 'schema-container-folder',
      name: pinRef.schema,
      parentId: null,
      metadata: {
        connectionId,
        database: pinRef.database,
        schema: pinRef.schema,
        objectCount: 0,
        engine,
      },
    } as unknown as DatabaseExplorerNode;
  }

  if (pinRef.kind === 'folder') {
    const FOLDER_TO_NODE_TYPE: Record<typeof pinRef.folder, string> = {
      tables: 'tables-folder',
      views: 'views-folder',
      storedProcedures: 'stored-procedures-folder',
      functions: 'functions-folder',
      programmability: 'programmability-folder',
    };
    return {
      ...node,
      id: node.id,
      type: FOLDER_TO_NODE_TYPE[pinRef.folder],
      name: pinRef.folder,
      parentId: null,
      metadata: { connectionId, engine, database: pinRef.database },
    } as unknown as DatabaseExplorerNode;
  }

  // pinRef.kind === 'object'
  const OBJECT_TO_NODE_TYPE: Record<typeof pinRef.objectType, string> = {
    table: 'table',
    view: 'view',
    storedProcedure: 'stored-procedure',
    function: 'function',
  };
  return {
    ...node,
    id: node.id,
    type: OBJECT_TO_NODE_TYPE[pinRef.objectType],
    name: pinRef.name,
    parentId: null,
    metadata: {
      objectDetails: {
        name: pinRef.name,
        schema: pinRef.schema,
        connectionId,
        // `id` is optional downstream — fetchers fall back to name-based
        // lookup when missing (see objectDefinitionService.ts).
        id: '',
        type: OBJECT_TO_NODE_TYPE[pinRef.objectType],
        metadata: { database: pinRef.database },
      },
    },
  } as unknown as DatabaseExplorerNode;
}

interface DatabaseExplorerProps {
  activeConnections: ActiveConnection[];
  onNodeSelect?: (nodeId: string) => void;
  onRefreshRequest?: () => void;
  onAddConnection?: () => void;
  onDisconnect?: (connectionId: string) => Promise<void>;
  onReconnect?: (connectionId: string) => Promise<boolean>;
  className?: string;
}

export function DatabaseExplorer({
  activeConnections,
  onNodeSelect,
  onRefreshRequest,
  onAddConnection,
  onDisconnect,
  onReconnect,
  className,
}: DatabaseExplorerProps) {
  const { selectedNodeId, selectedConnectionId, setSelectedNodeId, setSelectedConnectionId, setSelectedDatabase, highlightedNodeId, clearHighlightedNodeId } = useDatabaseUIStore();
  const {
    treeData,
    setTreeData,
    toggleNodeExpanded,
    updateNode,
    addDatabaseObjects,
    setLoadingObject,
    setPreloadedData,
    mergePreloadedObjects,
    beginPreload,
    endPreload,
    getPreloadedObjects,
    getPreloadRevision,
    isPreloading,
    collapseAll,
  } = useDatabaseStore();

  const {
    fetchDatabasesForConnection,
    fetchObjectsForFolder,
    invalidateDatabases,
    invalidateObjects,
  } = useDatabaseFetch();

  const markConnectionDisconnected = useConnectionStore((state) => state.markConnectionDisconnected);
  const updateConnectionStatus = useSessionStore((state) => state.updateConnectionStatus);
  const collapseSidebar = useSidebarStore((state) => state.collapse);
  const jobSchedulerSupportVersion = useJobSchedulerEngineSupportVersion();
  // T12 — Object Explorer's "AI" submenu entries (table/view context menu)
  // launch through the shared task launcher; `cliRequiredOpen` backs the
  // dialog rendered near the bottom of this component's JSX. `cliAvailable` /
  // `cliDetectionLoading` are threaded down through `DatabaseExplorerTree` →
  // `Explorer` → `TreeContext` so every table/view row's "AI" submenu reads
  // gating state from ONE hook call here instead of each row calling
  // `useCliDetection()` itself (T12 fix round 1, M-4 — was one IPC probe +
  // one permanent listener per row on a cold cache; see task-T12-report.md).
  const {
    launchTask,
    cliAvailable: aiCliAvailable,
    cliDetectionLoading: aiCliDetectionLoading,
    cliRequiredOpen,
    setCliRequiredOpen,
  } = useAiTaskLauncher();
  // The TanStack Query cache backs the pin-resolution classifier — we hand the
  // live client to the rebuild-on-pin-change subscriber so freshly-rebuilt
  // pinned children carry their correct `resolved` / `stale` / `unresolved`
  // state instead of the factory-default `unresolved`.
  const queryClient = useQueryClient();

  // S23 — conditional "Dashboards" root node. `dashboards` drives the
  // identity-preserving rebuild effect below; the rename dialog target is
  // local UI state (mirrors DashboardTab.tsx's own `settingsOpen` pattern,
  // reusing the same dialog component for a single settings surface).
  const dashboards = useDashboardStore((s) => s.dashboards);
  const dashboardsBetaEnabled = useBetaFeature('dashboards');
  const [dashboardRenameTarget, setDashboardRenameTarget] = useState<Dashboard | null>(null);

  // Track previously seen connection IDs to detect changes
  const prevConnectionIdsRef = useRef<Set<string>>(new Set());
  const prevJobSchedulerSupportVersionRef = useRef(jobSchedulerSupportVersion);
  // Track previous connection statuses to detect reconnections
  const prevConnectionStatusRef = useRef<Map<string, string | undefined>>(new Map());
  /** Last `connectedAt` seen per connection — a change means a NEW connect. */
  const prevConnectedAtRef = useRef<Map<string, number>>(new Map());
  /** Ids that were CONNECTED in the previous snapshot — see the reconnect effect. */
  const prevConnectedIdsRef = useRef<Set<string>>(new Set());
  // Track newly added connections for highlighting/scrolling
  const [highlightedConnectionIds, setHighlightedConnectionIds] = useState<Set<string>>(new Set());
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  // Export dialog state
  const [exportDialogState, setExportDialogState] = useState<{
    open: boolean;
    connectionId: string;
    databaseName: string;
  }>({ open: false, connectionId: '', databaseName: '' });
  // Import dialog state
  const [importDialogState, setImportDialogState] = useState<{
    open: boolean;
    connectionId: string;
  }>({ open: false, connectionId: '' });
  // Backup .bak dialog state
  const [backupBakDialogState, setBackupBakDialogState] = useState<{
    open: boolean;
    connectionId: string;
    databaseName: string;
  }>({ open: false, connectionId: '', databaseName: '' });
  // Restore .bak dialog state
  const [restoreBakDialogState, setRestoreBakDialogState] = useState<{
    open: boolean;
    connectionId: string;
    databaseName: string;
  }>({ open: false, connectionId: '', databaseName: '' });
  // PostgreSQL backup dialog state
  const [pgsqlBackupDialogState, setPgsqlBackupDialogState] = useState<{
    open: boolean;
    connectionId: string;
    databaseName: string;
  }>({ open: false, connectionId: '', databaseName: '' });
  // PostgreSQL restore dialog state
  const [pgsqlRestoreDialogState, setPgsqlRestoreDialogState] = useState<{
    open: boolean;
    connectionId: string;
    databaseName: string;
  }>({ open: false, connectionId: '', databaseName: '' });
  // Clone database dialog state
  const [cloneDatabaseDialogState, setCloneDatabaseDialogState] = useState<{
    open: boolean;
    connectionId: string;
    databaseName: string;
  }>({ open: false, connectionId: '', databaseName: '' });
  // Clone table dialog state
  const [cloneTableDialogState, setCloneTableDialogState] = useState<{
    open: boolean;
    connectionId: string;
    databaseName: string;
    schema: string;
    tableName: string;
  }>({ open: false, connectionId: '', databaseName: '', schema: '', tableName: '' });
  // Clone table progress dialog state. The target coordinates are kept so the
  // Tables folder can be refreshed when the clone completes.
  const [cloneTableProgressState, setCloneTableProgressState] = useState<{
    open: boolean;
    operationId: string;
    sourceTableName: string;
    targetTableName: string;
    targetConnectionId: string;
    targetDatabaseName: string;
  }>({
    open: false,
    operationId: '',
    sourceTableName: '',
    targetTableName: '',
    targetConnectionId: '',
    targetDatabaseName: '',
  });
  // Rename pin dialog state (Task 16 — pinned-workspace "Rename…")
  const [renamePinDialogState, setRenamePinDialogState] = useState<{
    open: boolean;
    connectionId: string;
    ref: PinnedWorkspaceRef | null;
  }>({ open: false, connectionId: '', ref: null });
  const { track } = useAnalytics();

  // Callback when database preload state changes - updates tree node to show loading indicator
  const handleDatabasePreloadStateChange = useCallback((connectionId: string, database: string, isPreloading: boolean) => {
    const nodeId = createDatabaseNodeId(connectionId, database);
    const currentTreeData = useDatabaseStore.getState().treeData;
    const existingNode = findNodeById(nodeId, currentTreeData) as DatabaseTreeNode | undefined;
    // Nothing to mark when the node is not in the tree: `updateNode` would be a
    // no-op that still rebuilds the whole tree array.
    if (!existingNode) return;
    updateNode(nodeId, {
      metadata: {
        ...(existingNode?.metadata ?? {}),
        connectionId,
        name: database,
        isPreloadingObjects: isPreloading,
      },
    });
  }, [updateNode]);

  // Helper to restructure database children when schema grouping mode changes
  const restructureDatabaseChildren = useCallback(
    (
      connectionId: string,
      database: string,
      objects: DatabaseObject[],
      mode: 'auto' | 'always' | 'never',
      opts: { resetFolders?: boolean; allowLayoutChange?: boolean } = {},
    ) => {
      const dbNodeId = createDatabaseNodeId(connectionId, database);
      const currentTreeData = useDatabaseStore.getState().treeData;
      const dbNode = findNodeById(dbNodeId, currentTreeData);
      if (!dbNode) return;

      const engine = (dbNode.metadata as { engine?: string }).engine as
        | 'mssql' | 'pgsql' | 'mysql' | 'oracle' | 'sqlite' | 'mock' | undefined;
      const currentChildren = (dbNode.children ?? []) as DatabaseExplorerNode[];

      // Never re-lay-out a database the user has open. A flat ⇄ grouped flip
      // renames every folder id, so `carryOverViewState` has nothing to match
      // and the subtree they are clicking through is replaced under them.
      //
      // Only the LAYOUT is deferred, never the contents: rebuilding in the
      // shape that is already on screen keeps every id (and so every expansion)
      // while still adding, removing and replacing the object nodes underneath.
      // Skipping the rebuild outright stranded new metadata — under a grouped
      // layout `addDatabaseObjectsToTree` targets flat folder ids that do not
      // exist, so nothing else would have written it.
      const grouped = isCurrentlyGrouped(currentChildren);
      const keepLayout = shouldKeepCurrentLayout({
        currentChildren,
        willGroup: shouldGroupBySchema(mode, objects),
        allowLayoutChange: opts.allowLayoutChange,
      });
      const effectiveMode = keepLayout ? (grouped ? 'always' : 'never') : mode;

      const newChildren = applySchemaGroupingToDatabaseNode({
        currentChildren,
        objects,
        mode: effectiveMode,
        connectionId,
        database,
        engine,
        resetFolders: opts.resetFolders,
      });

      // Forcing the current shape can still fail to reproduce it — a grouped
      // database whose objects no longer have an eligible schema. Rather than
      // flip anyway, leave it and let the next explicit Refresh decide.
      if (keepLayout && grouped !== isCurrentlyGrouped(newChildren)) return;

      // The preloader restructures every database once its objects land. When the
      // layout is unchanged (the common flat case) that write would remount the
      // subtree for nothing, collapsing folders and eating in-flight clicks.
      if (isSameChildList(newChildren, currentChildren)) return;

      updateNode(dbNodeId, { children: newChildren });
    },
    [updateNode],
  );

  // Which connect a preload belongs to. Bumped on disconnect / removal / a
  // force Reconnect that rebuilds in place, so work from a dead connect goes
  // quiet instead of publishing over the live one (`connectionGenerations.ts`).
  const generations = useMemo(() => createConnectionGenerations(), []);

  // One LIVE preload wave per connection at a time — a forced refresh no longer
  // races the wave already running, and never queues behind an obsolete one
  // (see `connectionWaveQueue.ts`).
  const waveQueue = useMemo(
    () => createConnectionWaveQueue({ isCurrent: generations.isCurrent }),
    [generations],
  );

  /**
   * Every object listing the explorer issues, fenced on the connect generation
   * live when it STARTS. The user-expand bypass in `fetchObjectsThroughCache`
   * runs outside `fetchQuery` and so outside `cancelQueries` — this is the only
   * thing that stops a dead connect's rows reaching the tree through it.
   */
  const fetchObjectsForFolderFenced = useCallback(
    (
      objectType: DatabaseObjectType,
      connectionId: string,
      database: string,
      origin?: ExplorerFetchOrigin,
    ) => {
      const generation = generations.current(connectionId);
      return fetchObjectsForFolder(objectType, connectionId, database, origin, () =>
        generations.isCurrent(connectionId, generation),
      );
    },
    [fetchObjectsForFolder, generations],
  );

  /**
   * THE one place a connect ends. Bumps the generation (everything captured
   * before it is now stale), drops the connect's bookkeeping, and ends the
   * query lifecycle it shared.
   *
   * The query cancel/remove is not optional: `useDatabaseQueries` keys object
   * listings by `(objectType, connectionId, database)` alone, so a successor
   * preload asking for the same key JOINS the predecessor's in-flight query and
   * publishes ITS rows — the successor's own fence reads "live", because the
   * staleness is in the shared promise, not in the caller. Cancelling and
   * removing (rather than versioning the key) keeps normal refresh caching
   * exactly as it was.
   */
  const endConnectionGeneration = useCallback((connectionId: string) => {
    generations.bump(connectionId);
    waveQueue.forget(connectionId);
    // The generation owns `isPreloading`. Whatever was still running is now
    // fenced and will never reach `setPreloadedData`, so nothing else can
    // clear the flag — and a stuck flag gates the reconnect's listing, the
    // folder expand and the connection-node toggle.
    endPreload(connectionId);
    // Shared with `connectionListingLifecycle`, which runs the same eviction off
    // the connection store so it still happens while this component is
    // unmounted (a collapsed Explorer pane). Both halves are idempotent.
    evictConnectionObjectQueries(queryClient, connectionId);
    // Azure Storage listings (spec §5.1 key shape: connection id at [1]) end
    // with the connect too. Only here, not in the store-driven lifecycle: an
    // open storage tab resets its own listing once per connection generation
    // (`useStorageListingReconnect`), and a synchronous eviction inside the
    // connection store's `set()` would land before that reset and could list twice.
    const isStorageListingOfConnection = (queryKey: readonly unknown[]) =>
      isStorageListingQueryKey(queryKey) && queryKey[1] === connectionId;
    void queryClient.cancelQueries({ predicate: (q) => isStorageListingOfConnection(q.queryKey) });
    queryClient.removeQueries({ predicate: (q) => isStorageListingOfConnection(q.queryKey) });
  }, [generations, waveQueue, queryClient, endPreload]);

  // Callback to preload objects after databases are loaded. `generation` is the
  // one captured BEFORE the listing started — see `listDatabasesFolder`.
  const handleDatabasesLoaded = useCallback((connectionId: string, databases: string[], generation: number) => {
    if (!generations.isCurrent(connectionId, generation)) return;
    const state = useConnectionStore.getState();
    const active = state.activeConnections?.find((c) => c.id === connectionId);
    const connection =
      state.connections?.find((c) => c.id === connectionId) ?? active?.connection;
    const engine = connection?.engine as DatabaseEngineId | undefined;

    // D3 — one event per connect: connect → database list rendered.
    trackExplorerDatabasesLoaded({ connectionId, engine, connectedAt: active?.connectedAt });

    // Home database first and alone at the head of the wave. Resolved against
    // the LISTING's own spelling, which is what everything downstream is keyed
    // by.
    const plan = planPreloadOrder(databases, resolveHomePreloadDatabase(connection, engine), engine);

    // Background preload, queued behind any wave still running for this
    // connection (a forced refresh can now start one while another is in
    // flight); never awaited by the caller.
    void waveQueue.enqueue(connectionId, generation, () => {
      // Raised HERE, not at enqueue time: a wave queued behind another would
      // otherwise have its flag cleared by the FIRST wave's completion and run
      // the rest of its databases with the flag already down.
      beginPreload(connectionId);
      return preloadObjectsForConnection({
        connectionId,
        isStale: () => !generations.isCurrent(connectionId, generation),
        databases: plan.databases,
        homeDatabase: plan.homeDatabase,
        concurrency: getPreloadConcurrency(engine),
        fetchObjectsForFolder: fetchObjectsForFolderFenced,
        setPreloadedData,
        readPreloadRevision: getPreloadRevision,
        mergePreloadedObjects,
        addDatabaseObjects,
        onDatabasePreloadStateChange: handleDatabasePreloadStateChange,
        onDatabasePreloaded: (connId, database, objects) => {
          const mode = useAppStore.getState().objectExplorerGrouping;
          restructureDatabaseChildren(connId, database, objects, mode);
        },
      });
    });
  }, [fetchObjectsForFolderFenced, setPreloadedData, mergePreloadedObjects, addDatabaseObjects, handleDatabasePreloadStateChange, restructureDatabaseChildren, waveQueue, generations, beginPreload, getPreloadRevision]);

  /**
   * A generation-scoped "is this connect over?" predicate, captured NOW. Every
   * listing an expand starts is fenced on it.
   */
  const isConnectStale = useCallback(
    (connectionId: string) => {
      const generation = generations.current(connectionId);
      return () => !generations.isCurrent(connectionId, generation);
    },
    [generations],
  );

  /**
   * THE one way a databases-folder listing is started.
   *
   * The generation is captured HERE, before the fetch, and every consequence of
   * that listing is fenced on it: the tree write, the two callbacks, the
   * readiness event, and the home-preload bookkeeping they consume. Capturing
   * it when the listing RETURNS would let a held listing from a dead connect
   * rebuild the live tree, eat the live connect's bookkeeping and report its
   * latency.
   */
  const listDatabasesFolder = useCallback(
    (args: {
      node: FolderTreeNode;
      connectionId: string;
      engine?: DatabaseEngineId;
      forceReload?: boolean;
      /** Connection-lost detection, used by the user-driven expand path. */
      onFetchError?: (connectionId: string, errorMessage: string) => void;
    }): Promise<void> => {
      const { connectionId } = args;
      const generation = generations.current(connectionId);
      return loadDatabasesFolder({
        node: args.node,
        connectionId,
        engine: args.engine,
        isPreloading,
        fetchDatabasesForConnection,
        updateNode,
        isStale: () => !generations.isCurrent(connectionId, generation),
        onDatabasesLoaded: (id, databases) => handleDatabasesLoaded(id, databases, generation),
        ...(args.onFetchError ? { onFetchError: args.onFetchError } : {}),
        ...(args.forceReload ? { forceReload: true } : {}),
      });
    },
    [generations, isPreloading, fetchDatabasesForConnection, updateNode, handleDatabasesLoaded],
  );

  // Re-fetch + rebuild a single database's object subtree. Used when objects are
  // created out-of-band (e.g. a cross-engine migration) and the tree would
  // otherwise show stale contents until a manual refresh. Invalidating the
  // cached object listings first guarantees the re-preload reflects the new
  // tables/schema; restructureDatabaseChildren (via onDatabasePreloaded) then
  // surfaces a brand-new schema folder, which a per-folder refresh cannot.
  const refreshDatabaseObjects = useCallback(
    (connectionId: string, database: string) => {
      if (!connectionId || !database) return;
      const dbNodeId = createDatabaseNodeId(connectionId, database);
      // Nothing to refresh if the target connection isn't in the tree (not
      // open / not connected) — the next expand fetches fresh data anyway.
      if (!findNodeById(dbNodeId, useDatabaseStore.getState().treeData)) return;

      const PRELOAD_TYPES: DatabaseObjectType[] = [
        'table', 'view', 'stored-procedure', 'function', 'package',
      ];
      for (const objectType of PRELOAD_TYPES) {
        invalidateObjects(objectType, connectionId, database);
      }
      // Same fence and same queue as every other wave: a held refresh must not
      // land its (old) database list on the next connection under this id, and
      // must not run alongside the wave a reconnect started.
      const generation = generations.current(connectionId);
      void waveQueue.enqueue(connectionId, generation, () => {
        // Every wave raises the flag when it STARTS — this one queues behind
        // whatever is already running, whose completion lowered it.
        beginPreload(connectionId);
        return preloadObjectsForConnection({
          connectionId,
          isStale: () => !generations.isCurrent(connectionId, generation),
          databases: [database],
          // Resolved INSIDE the wave, not at enqueue time: while the first
          // wave is still running the entry's `databases` is `[]` (present but
          // empty), so a queued refresh that captured it up front would
          // republish an empty list over the full one. An empty array counts
          // as absent for the same reason.
          publishedDatabases:
            useDatabaseStore.getState().preloadedData[connectionId]?.databases?.length
              ? useDatabaseStore.getState().preloadedData[connectionId].databases
              : [database],
          fetchObjectsForFolder: fetchObjectsForFolderFenced,
          setPreloadedData,
          readPreloadRevision: getPreloadRevision,
          mergePreloadedObjects,
          addDatabaseObjects,
          onDatabasePreloadStateChange: handleDatabasePreloadStateChange,
          onDatabasePreloaded: (connId, db, objects) => {
            const mode = useAppStore.getState().objectExplorerGrouping;
            // The object caches were just invalidated, so any children still hanging
            // off the tree are stale — drop them and let the next expand re-fetch.
            restructureDatabaseChildren(connId, db, objects, mode, {
              resetFolders: true,
              allowLayoutChange: true,
            });
          },
        });
      });
    },
    [invalidateObjects, fetchObjectsForFolderFenced, setPreloadedData, mergePreloadedObjects, addDatabaseObjects, handleDatabasePreloadStateChange, restructureDatabaseChildren, generations, waveQueue, getPreloadRevision, beginPreload],
  );

  // Bridge the renderer-only refresh bus (bumped by e.g. the migration store)
  // to refreshDatabaseObjects. A ref keeps the latest callback without making
  // the effect re-run on its identity — it should fire only on a new request.
  const refreshDatabaseObjectsRef = useRef(refreshDatabaseObjects);
  refreshDatabaseObjectsRef.current = refreshDatabaseObjects;
  const objectExplorerRefreshNonce = useObjectExplorerRefreshStore((s) => s.nonce);
  useEffect(() => {
    if (objectExplorerRefreshNonce === 0) return; // initial mount — no request yet
    const { connectionId, database } = useObjectExplorerRefreshStore.getState();
    if (connectionId && database) {
      refreshDatabaseObjectsRef.current(connectionId, database);
    }
  }, [objectExplorerRefreshNonce]);

  useEffect(() => {
    const currentConnectionIds = new Set(activeConnections.map(c => c.id));
    const prevConnectionIds = prevConnectionIdsRef.current;

    // Check if connection IDs have actually changed
    const hasChanges =
      currentConnectionIds.size !== prevConnectionIds.size ||
      [...currentConnectionIds].some(id => !prevConnectionIds.has(id)) ||
      [...prevConnectionIds].some(id => !currentConnectionIds.has(id));
    const supportChanged = prevJobSchedulerSupportVersionRef.current !== jobSchedulerSupportVersion;

    // If the tree was cleared (e.g. disconnect removed nodes) but we still have
    // connections to show, rebuild the tree even if the connection IDs didn't change.
    const shouldRebuildEmptyTree = treeData.length === 0 && activeConnections.length > 0;

    if (!hasChanges && !shouldRebuildEmptyTree && !supportChanged) {
      return;
    }

    // Update the ref for next comparison
    prevConnectionIdsRef.current = currentConnectionIds;
    prevJobSchedulerSupportVersionRef.current = jobSchedulerSupportVersion;

    // A closed connection keeps no connect-latency latch: the
    // next connect under the same id is a new connect. Driven off the PREVIOUS
    // id set: anything keyed on preload bookkeeping released nothing on an
    // ordinary disconnect, and the telemetry latch then swallowed the
    // reconnect's event.
    forgetRemovedConnections(prevConnectionIds, currentConnectionIds, (id) => {
      endConnectionGeneration(id);
      prevConnectedAtRef.current.delete(id);
    });

    if (activeConnections.length === 0) {
      // S23 — the "Dashboards" root is independent of connections (zero
      // connections is a normal state for a dashboards-only user); clearing
      // ALL roots here would wipe it until the next `dashboards` store
      // change. Preserve it, drop everything else.
      setTreeData(treeData.filter((n) => n.type === 'dashboards-root'));
      setHighlightedConnectionIds(new Set());
      return;
    }

    // If tree is empty, build from scratch
    if (treeData.length === 0) {
      const newTree = buildDatabaseTree(activeConnections);

      setTreeData(newTree);

      // Databases folder is expanded by default - load databases for each active connection
      newTree.forEach((node) => {
        if (node.type !== 'connection') return;

        const connectionNode = node as ConnectionTreeNode;
        const connectionId = connectionNode.metadata.id;

        // Skip disconnected connections - they need to reconnect first
        if (connectionNode.metadata.status === 'disconnected') return;

        // Also check if the connection is actually active
        const activeConnection = activeConnections.find(c => c.id === connectionId);
        if (activeConnection?.status === 'disconnected') return;

        const databasesFolder = node.children?.find(
          child => child.type === 'databases-folder'
        ) as FolderTreeNode | undefined;

        if (!databasesFolder) return;


        void listDatabasesFolder({
          node: databasesFolder,
          connectionId,
          engine: connectionNode.metadata.engine as DatabaseEngineId | undefined,
        });
      });
      return;
    }

    // Merge new connections into existing tree, preserving state
    const { tree, newConnectionIds } = mergeConnectionsIntoTree(treeData, activeConnections);

    // Update tree FIRST, before any async operations
    setTreeData(tree);

    // Track new connections for highlighting
    if (newConnectionIds.length > 0) {
      setHighlightedConnectionIds(prev => {
        const next = new Set(prev);
        newConnectionIds.forEach(id => next.add(id));
        return next;
      });

      // Auto-fetch databases for new active connections immediately
      newConnectionIds.forEach(connId => {
        // Skip disconnected connections
        const activeConnection = activeConnections.find(c => c.id === connId);
        if (activeConnection?.status === 'disconnected') return;

        const connectionNode = tree.find(
          node => node.type === 'connection' && node.metadata?.id === connId
        );
        if (!connectionNode) return;

        const databasesFolder = connectionNode.children?.find(
          child => child.type === 'databases-folder'
        ) as FolderTreeNode | undefined;

        if (databasesFolder) {

          void listDatabasesFolder({
            node: databasesFolder,
            connectionId: connId,
            engine: (connectionNode as ConnectionTreeNode).metadata.engine as DatabaseEngineId | undefined,
          });
        }
      });
    }
  }, [activeConnections, setTreeData, treeData, listDatabasesFolder, endConnectionGeneration, jobSchedulerSupportVersion]);

  // Hydrate the per-connection pinned-objects store on mount/connection-add.
  // `hydrate()` is idempotent — first call subscribes to onChange so subsequent
  // mutations push back into the store. Safe to invoke per render when the
  // connection list changes; we never call hydrate from inside the subscriber
  // below, so there is no feedback loop.
  useEffect(() => {
    const ids = activeConnections.map((c) => c.id);
    ids.forEach((id) => { void usePinnedObjectsStore.getState().hydrate(id); });
  }, [activeConnections]);

  // G5 — start the AUTHORITATIVE all-tabs `sourcePin` reconciler once.
  // `useWorkspacePinState` (Task 18) already severs a single MOUNTED tab's
  // link live; this reconciler additionally covers tabs whose pin button
  // isn't currently mounted, and the post-session-restore case (session
  // restore reconnects, which re-triggers the `hydrate()` effect above and
  // lands a fresh `byConnection` slot this subscriber reacts to). Started
  // once for the component's lifetime — `DatabaseExplorer` is the sidebar,
  // mounted for the app's lifetime — and torn down on unmount.
  useEffect(() => {
    return startSourcePinReconciler();
  }, []);

  // Azure Storage: one account-info probe per connect; rebuilds the storage
  // branch (folders omitted for unavailable services) when it answers.
  useStorageAccountInfoSync();

  // Re-inject pinned children into the connection node whenever the store's
  // pinned list for that connection changes. We read the current tree from
  // the store (not the closure) so we never overwrite intermediate updates.
  useEffect(() => {
    const unsub = usePinnedObjectsStore.subscribe((state, prev) => {
      const currentTree = useDatabaseStore.getState().treeData;
      for (const connNode of currentTree) {
        if (connNode.type !== 'connection') continue;
        const connectionId = (connNode as ConnectionTreeNode).metadata.id;
        const currentPins = state.byConnection[connectionId]?.pinnedObjects ?? [];
        const prevPins = prev.byConnection[connectionId]?.pinnedObjects ?? [];
        if (currentPins === prevPins) continue;

        const engine = ((connNode as ConnectionTreeNode).metadata.engine ?? 'mssql') as string;
        const newPinned = buildPinnedChildren({
          connectionId,
          parentId: connNode.id,
          pins: currentPins,
          engine,
          queryClient,
        });
        const existingChildren = (connNode.children ?? []) as DatabaseExplorerNode[];
        const isPinned = (c: DatabaseExplorerNode) =>
          c.type === 'pinned-database' ||
          c.type === 'pinned-schema' ||
          c.type === 'pinned-folder' ||
          c.type === 'pinned-object' ||
          c.type === 'pinned-workspace' ||
          c.type === 'pinned-storage';
        // Carry over expanded pinned subtrees (review finding P2) — the rebuild
        // is collapsed-by-default, so without this an add/unpin/reorder would
        // wipe whatever the user had drilled into.
        const mergedPinned = preservePinnedExpansionState(
          newPinned,
          existingChildren.filter(isPinned),
        );
        // Strip the leading run of pinned-* siblings; keep everything else.
        const rest = existingChildren.filter((c) => !isPinned(c));
        updateNode(connNode.id, { children: [...mergedPinned, ...rest] });
      }
    });
    return unsub;
  }, [updateNode, queryClient]);

  // Task 12 / Task 11 follow-up — subscribe to the TanStack Query cache so
  // pinned-row resolution upgrades when the underlying `database-objects`
  // listings land. Without this, cold-boot pins read from disk would stay
  // visually "unresolved" (greyed) forever until the user touched the pin,
  // because the original pin-store subscriber only fires on pin mutations.
  //
  // No infinite loop: this effect READS the cache, classifies pins, and
  // writes to the explorer tree via `updateNode`. The tree update never
  // enqueues a `database-objects` query, so the only retrigger path is when
  // a fetch actually finishes (the very event we're listening for). After
  // the rebuild, the new pinned children also don't subscribe to queries.
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsub = cache.subscribe((evt) => {
      if (evt.type !== 'updated') return;
      const key = evt.query.queryKey;
      // Cheap early-out: 99% of cache events are for unrelated queries.
      if (!Array.isArray(key)) return;
      // Both `databaseKey` and `objectKey` (see useDatabaseQueries.ts) put the
      // connectionId at index [2]; the Azure Storage listing keys
      // (`['azure-storage', connectionId, …]`, spec §5.1) put it at [1] — a
      // storage pin resolves against that cache.
      const connectionId =
        key[0] === 'database-objects' && typeof key[2] === 'string'
          ? key[2]
          : isStorageListingQueryKey(key)
            ? key[1]
            : undefined;
      if (!connectionId) return;

      const pinsForConn =
        usePinnedObjectsStore.getState().byConnection[connectionId]?.pinnedObjects;
      if (!pinsForConn || pinsForConn.length === 0) return;

      const currentTree = useDatabaseStore.getState().treeData;
      const connNode = currentTree.find(
        (n) => n.type === 'connection' && (n as ConnectionTreeNode).metadata.id === connectionId,
      ) as ConnectionTreeNode | undefined;
      if (!connNode) return;

      const engine = (connNode.metadata.engine ?? 'mssql') as string;
      const newPinned = buildPinnedChildren({
        connectionId,
        parentId: connNode.id,
        pins: pinsForConn,
        engine,
        queryClient,
      });
      const existingChildren = (connNode.children ?? []) as DatabaseExplorerNode[];

      // Review finding #9 — every background `database-objects` cache update
      // used to rebuild the connection's children array unconditionally,
      // cascading re-renders down the whole subtree (lost scroll position,
      // expanded-state churn). Skip the updateNode call when the pinned-
      // children resolution hasn't actually changed since the last rebuild.
      const existingPinned = existingChildren.filter(
        (c) =>
          c.type === 'pinned-database' ||
          c.type === 'pinned-schema' ||
          c.type === 'pinned-folder' ||
          c.type === 'pinned-object' ||
          c.type === 'pinned-workspace' ||
          c.type === 'pinned-storage',
      );
      if (
        existingPinned.length === newPinned.length &&
        existingPinned.every((prev, i) => {
          const next = newPinned[i] as unknown as { id: string; resolution?: string };
          return (
            (prev as unknown as { id: string }).id === next.id &&
            (prev as unknown as { resolution?: string }).resolution === next.resolution
          );
        })
      ) {
        // Same ids in same order with same resolution states — nothing for
        // the tree to react to.
        return;
      }

      const rest = existingChildren.filter(
        (c) =>
          c.type !== 'pinned-database' &&
          c.type !== 'pinned-schema' &&
          c.type !== 'pinned-folder' &&
          c.type !== 'pinned-object' &&
          c.type !== 'pinned-workspace' &&
          // Without this row the previous `pinned-storage` node survived in
          // `rest` and was re-appended after its rebuilt twin, so a storage pin
          // rendered twice — the old copy frozen at its earlier resolution
          // (`storage-pins.spec.ts`: a removed container never read "stale").
          c.type !== 'pinned-storage',
      );
      // Carry over expanded pinned subtrees (review finding P2) — a background
      // resolution upgrade must not collapse rows the user drilled into.
      const mergedPinned = preservePinnedExpansionState(newPinned, existingPinned);
      updateNode(connNode.id, { children: [...mergedPinned, ...rest] });
    });
    return unsub;
  }, [queryClient, updateNode]);

  // S23 — keep the conditional "Dashboards" root node in sync with the
  // dashboards store: absent at 0, appears at 1, disappears when the last
  // one is deleted, rebuilt in place otherwise (identity-preserving — same
  // rebuild-race lesson as the pinned-objects subscriber above). Reads the
  // CURRENT tree from the store (not the `treeData` closure) so this never
  // clobbers an intermediate update from another effect in the same tick.
  useEffect(() => {
    const currentTree = useDatabaseStore.getState().treeData;
    const existingRoot = currentTree.find(
      (n): n is DashboardsRootTreeNode => n.type === 'dashboards-root',
    );
    // Beta off: treat it as "no dashboards" so the SAME removal branch below
    // takes the node out of the tree — the user's dashboards are untouched on
    // disk and the root reappears the moment they switch the feature back on.
    const nextRoot = dashboardsBetaEnabled
      ? buildDashboardsRootNode(dashboards, existingRoot ?? null)
      : null;

    if (!existingRoot && nextRoot) {
      useDatabaseStore.getState().setTreeData([...currentTree, nextRoot]);
      return;
    }
    if (existingRoot && !nextRoot) {
      useDatabaseStore.getState().setTreeData(currentTree.filter((n) => n.id !== existingRoot.id));
      return;
    }
    if (existingRoot && nextRoot && existingRoot.children !== nextRoot.children) {
      updateNode(existingRoot.id, { children: nextRoot.children, metadata: nextRoot.metadata });
    }
  }, [dashboards, dashboardsBetaEnabled, updateNode]);

  // Auto-select the connection when exactly one is connected. Count only
  // connected entries — the list may also carry disconnected placeholders
  // (restored sessions, saved browser-mode connections) that must not
  // suppress auto-selection of the single live connection.
  useEffect(() => {
    const connected = activeConnections.filter((c) => c.status === 'connected');
    if (connected.length === 1 && !selectedConnectionId) {
      setSelectedConnectionId(connected[0].id);
    }
  }, [activeConnections, selectedConnectionId, setSelectedConnectionId]);

  // Auto-select first non-system database when databases are loaded for a connection
  useEffect(() => {
    // Need a selected connection first
    if (!selectedConnectionId) return;

    // Skip if we already have a selected database
    const currentSelectedDatabase = useDatabaseUIStore.getState().selectedDatabase;
    if (currentSelectedDatabase) return;

    // Find the connection node in the tree
    const connectionNode = treeData.find(
      node => node.type === 'connection' && node.metadata?.id === selectedConnectionId
    );
    if (!connectionNode) return;

    // Find the databases folder
    const databasesFolder = connectionNode.children?.find(
      child => child.type === 'databases-folder'
    );
    if (!databasesFolder?.children) return;

    // Look for user databases (not in system folder)
    for (const child of databasesFolder.children) {
      if (child.type === 'database') {
        // Found a user database - select it
        const dbName = child.metadata?.name || child.name;
        if (dbName) {
          // Set both the database name and the node ID for visual selection.
          // V5 — pass the owning connection so the store pairs (and coerces)
          // against the CURRENT connection, not a stale pairing.
          setSelectedDatabase(dbName, selectedConnectionId);
          const nodeId = createDatabaseNodeId(selectedConnectionId, dbName);
          setSelectedNodeId(nodeId);
          return;
        }
      }
    }
  }, [treeData, selectedConnectionId, setSelectedDatabase, setSelectedNodeId]);

  // Re-apply schema grouping when the setting changes
  useEffect(() => {
    let prev = useAppStore.getState().objectExplorerGrouping;
    const unsub = useAppStore.subscribe((state) => {
      if (state.objectExplorerGrouping === prev) return;
      prev = state.objectExplorerGrouping;
      const mode = state.objectExplorerGrouping;

      const preloaded = useDatabaseStore.getState().preloadedData;
      for (const [connectionId, entry] of Object.entries(preloaded)) {
        const objectsByDb = entry?.objects ?? {};
        for (const [database, objects] of Object.entries(objectsByDb)) {
          // The user just changed the grouping setting — that IS the request.
          restructureDatabaseChildren(connectionId, database, objects as DatabaseObject[], mode, {
            allowLayoutChange: true,
          });
        }
      }
    });
    return unsub;
  }, [restructureDatabaseChildren]);

  // Detect when a connection status changes (connected ↔ disconnected) and update the tree
  useEffect(() => {
    const currentStatusMap = new Map<string, string | undefined>();
    activeConnections.forEach(conn => {
      currentStatusMap.set(conn.id, conn.status);
    });

    const prevStatusMap = prevConnectionStatusRef.current;
    const reconnectedConnectionIds: string[] = [];
    const disconnectedConnectionIds: string[] = [];

    // Find connections that changed status
    activeConnections.forEach(conn => {
      const prevStatus = prevStatusMap.get(conn.id);
      const currentStatus = conn.status;

      // Reconnected: was 'disconnected', now is undefined or 'connected'
      if (prevStatus === 'disconnected' && currentStatus !== 'disconnected') {
        reconnectedConnectionIds.push(conn.id);
      }
      // Disconnected: was not 'disconnected', now is 'disconnected'
      else if (prevStatus !== 'disconnected' && currentStatus === 'disconnected') {
        disconnectedConnectionIds.push(conn.id);
      }
    });

    // Update the ref for next comparison
    prevConnectionStatusRef.current = currentStatusMap;

    // A disconnect ends the connect this session measured and preloaded for:
    // drop both latches so the next connect starts a fresh home preload and
    // reports its own connect → list latency.
    disconnectedConnectionIds.forEach((connId) => {
      endConnectionGeneration(connId);
      forgetExplorerConnectLatency(connId);
    });

    // Handle newly disconnected connections - update tree node status
    if (disconnectedConnectionIds.length > 0 && treeData.length > 0) {
      disconnectedConnectionIds.forEach(connId => {
        const connectionNode = treeData.find(
          node => node.type === 'connection' && node.metadata?.id === connId
        ) as ConnectionTreeNode | undefined;
        if (!connectionNode) return;

        // Update connection node metadata to show 'disconnected' status
        updateNode(connectionNode.id, {
          metadata: {
            ...connectionNode.metadata,
            status: 'disconnected',
          },
        });

        // A Databases folder that never got its listing (the connection dropped
        // while connecting, or a restore seeded it closed) is expanded with no
        // children — which TreeNode renders as the loading skeleton, and nothing
        // will ever load it now. Collapse it so the row reads as disconnected
        // rather than as perpetually loading. A folder that DID load keeps its
        // children and its expanded state: a stale list is still information.
        const databasesFolder = connectionNode.children?.find(
          child => child.type === 'databases-folder'
        ) as FolderTreeNode | undefined;
        if (databasesFolder?.isExpanded && isDatabasesFolderUnloaded(databasesFolder)) {
          updateNode(databasesFolder.id, { isExpanded: false, isLoading: false });
        }
      });
    }

    // Handle reconnected connections - refresh databases
    if (reconnectedConnectionIds.length > 0 && treeData.length > 0) {
      reconnectedConnectionIds.forEach(connId => {
        // Find the connection node in the tree
        const connectionNode = treeData.find(
          node => node.type === 'connection' && node.metadata?.id === connId
        ) as ConnectionTreeNode | undefined;
        if (!connectionNode) return;

        // Update connection node metadata to remove 'disconnected' status
        updateNode(connectionNode.id, {
          metadata: {
            ...connectionNode.metadata,
            status: undefined, // Clear the disconnected status
          },
        });

        // Find the databases folder
        const databasesFolder = connectionNode.children?.find(
          child => child.type === 'databases-folder'
        ) as FolderTreeNode | undefined;

        if (databasesFolder) {
          // Clear existing children (error nodes) and invalidate cache
          invalidateDatabases(connId);

          // Reset the databases folder to trigger re-fetch. `isExpanded` is
          // re-asserted because a connection that came back may have been built
          // (or collapsed) while it was closed — the same rule the first build
          // uses: auto-expand unless the connection has pins, which own the
          // entry point.
          const hasPins =
            (usePinnedObjectsStore.getState().byConnection[connId]?.pinnedObjects ?? []).length > 0;
          updateNode(databasesFolder.id, {
            children: [],
            isLoading: false,
            isExpanded: !hasPins,
          });

          // Get engine from active connection

          // Re-fetch databases and trigger preloading for search. FORCED: the
          // previous connect may have left `isPreloading` set (a wave that was
          // fenced mid-flight), and an unforced listing would return at that
          // guard and leave the folder on its skeleton forever.
          void listDatabasesFolder({
            node: { ...databasesFolder, children: [] },
            connectionId: connId,
            engine: connectionNode.metadata.engine as DatabaseEngineId | undefined,
            forceReload: true,
          });
        }
      });
    }
  }, [activeConnections, treeData, invalidateDatabases, updateNode, listDatabasesFolder, endConnectionGeneration]);

  /**
   * Force Reconnect rebuilds a connection IN PLACE: same id, still `connected`,
   * only `connectedAt` is replaced. Neither the tree-build effect (ids
   * unchanged) nor the status effect (no 'disconnected' transition) reacts, so
   * without this the new connect gets no home preload, no fresh listing and no
   * readiness measurement — and the next manual Refresh would report the
   * connection's AGE as its latency.
   *
   * Treat it as what it is: a new connect generation. Bump, drop the previous
   * connect's bookkeeping, start the home preload, and force a re-listing —
   * whose `handleDatabasesLoaded` measures readiness and consumes the latch, so
   * a later plain Refresh stays silent.
   */
  useEffect(() => {
    const wasConnected = prevConnectedIdsRef.current;
    const nowConnected = new Set(
      activeConnections.filter((c) => c.status !== 'disconnected').map((c) => c.id),
    );
    prevConnectedIdsRef.current = nowConnected;

    for (const conn of activeConnections) {
      const connectedAt =
        conn.connectedAt instanceof Date ? conn.connectedAt.getTime() : undefined;
      if (connectedAt === undefined || !Number.isFinite(connectedAt)) continue;

      const previous = prevConnectedAtRef.current.get(conn.id);
      prevConnectedAtRef.current.set(conn.id, connectedAt);
      // First sighting, or the same connect: the normal paths own those.
      if (previous === undefined || previous === connectedAt) continue;
      // ONLY a rebuild in place. A connection that went through 'disconnected'
      // (or left the active list and came back) also lands a new `connectedAt`,
      // but the status effect / tree-build effect already list it — reacting
      // here too would double-list and invalidate the home preload the other path
      // just started.
      if (!wasConnected.has(conn.id) || !nowConnected.has(conn.id)) continue;

      const connectionNode = useDatabaseStore
        .getState()
        .treeData.find(
          (node) => node.type === 'connection' && node.metadata?.id === conn.id,
        ) as ConnectionTreeNode | undefined;
      const databasesFolder = connectionNode?.children?.find(
        (child) => child.type === 'databases-folder',
      ) as FolderTreeNode | undefined;
      if (!databasesFolder) continue;

      endConnectionGeneration(conn.id);

      const engine = connectionNode?.metadata.engine as DatabaseEngineId | undefined;
      void listDatabasesFolder({
        node: { ...databasesFolder, children: [] },
        connectionId: conn.id,
        engine,
        forceReload: true,
      });
    }
  }, [activeConnections, endConnectionGeneration, listDatabasesFolder]);

  // Handle highlight animation complete
  const handleHighlightComplete = useCallback((connectionId: string) => {
    setHighlightedConnectionIds(prev => {
      const next = new Set(prev);
      next.delete(connectionId);
      return next;
    });
  }, []);

  const storageActionRefreshRef = useRef<(nodeId: string) => void>(() => undefined);
  const handleNodeClick = useCallback(
    (nodeId: string) => {
      setSelectedNodeId(nodeId);
      const node = findNodeById(nodeId, treeData);
      if (node) {
        const connectionId =
          node.type === 'connection'
            ? (node as ConnectionTreeNode).metadata.id
            : (node as DatabaseTreeNode).metadata?.connectionId;

        if (connectionId) {
          setSelectedConnectionId(connectionId as string);
        }

        // Review finding #12 — pinned rows commonly jump to a database different
        // from the currently-selected one. Status-bar widgets and Query Editor
        // "current database" indicators read selectedDatabase, so clicking a
        // pinned cross-DB row must update it. The pinRef carries the database
        // name; pinned-database also carries it directly in metadata.
        if (
          node.type === 'pinned-database' ||
          node.type === 'pinned-schema' ||
          node.type === 'pinned-folder' ||
          node.type === 'pinned-object' ||
          node.type === 'pinned-workspace'
        ) {
          const pinRef = (node as { pinRef?: { database?: string } }).pinRef;
          const pinnedDatabase =
            pinRef?.database ??
            (node.metadata as { database?: string } | undefined)?.database;
          if (pinnedDatabase) {
            // Pass the connection id so the paired
            // `selectedDatabaseConnectionId` is stamped too. Object-pin ids
            // parse through `setSelectedNodeId`'s derivation (which already
            // pairs them), but workspace-pin ids carry no database segment —
            // without the explicit pair here, `getValidSelectedDatabase`
            // would treat the selection as invalid and drop it.
            setSelectedDatabase(pinnedDatabase, connectionId as string | undefined);
          }
        }

        // The *Show details* tile on a Databases-folder failure dispatches
        // the diagnostics dialog via this single integration point. Send is
        // intentionally only available from inside the dialog footer — the
        // OE tree exposes a single affordance to keep the row uncluttered.
        const action = (node.metadata as { action?: string })?.action;
        if (action === 'create-queue' && connectionId) {
          openCreateQueueDialog({ connectionId: connectionId as string, source: 'tree' });
        }
        if (action === 'show-connection-diagnostics' && connectionId) {
          const trigger =
            ((node.metadata as { diagnosticsTrigger?: string }).diagnosticsTrigger ??
              'list_failure') as
              | 'list_failure'
              | 'list_empty'
              | 'pool_open_failure'
              | 'manual';
          showConnectionDiagnostics({
            connectionId: connectionId as string,
            trigger,
          });
        }

        // The *Retry* tile re-runs the databases listing in place via the
        // same force path as the folder's context-menu Refresh — after a
        // transient failure (VPN blip, sleepy server) the user shouldn't
        // have to hunt for the Refresh action.
        if (action === 'retry-list-databases' && connectionId) {
          const parentFolder = findNodeById(node.parentId ?? '', treeData);
          if (parentFolder?.type === 'databases-folder') {
            const parentConnectionNode = treeData.find(
              (n) => n.type === 'connection' && (n as ConnectionTreeNode).metadata?.id === connectionId
            ) as ConnectionTreeNode | undefined;
            track('feature:retry_list_databases', {
              engine: (parentConnectionNode?.metadata?.engine as string) ?? 'unknown',
            });
            invalidateDatabases(connectionId as string);
            updateNode(parentFolder.id, { children: [], isLoading: true });
            void listDatabasesFolder({
              node: parentFolder as FolderTreeNode,
              connectionId: connectionId as string,
              engine: parentConnectionNode?.metadata?.engine as DatabaseEngineId | undefined,
              forceReload: true,
            });
          }
        }

        if (node.type === 'storage-action') {
          const actionId = (node.metadata as { actionId?: string }).actionId;
          // Review E F14: the same refresh as the context-menu path (`handleRefresh` is created below, hence the ref).
          if (actionId) executeStorageContextMenuAction(actionId, node as StorageMenuNode, { onRefresh: (id) => storageActionRefreshRef.current(id), track });
        }
      }

      if (onNodeSelect) {
        onNodeSelect(nodeId);
      }
    },
    [onNodeSelect, setSelectedConnectionId, setSelectedDatabase, setSelectedNodeId, treeData, track, invalidateDatabases, updateNode, listDatabasesFolder],
  );

  // Handler for connection errors during expansion - detects connection loss
  const handleFetchError = useCallback(
    (connectionId: string, errorMessage: string) => {
      // Find the connection to get its engine
      const connection = activeConnections.find((conn) => conn.id === connectionId);
      if (!connection) return;

      const engineId: DatabaseEngineId = (connection.connection.engine as DatabaseEngineId) || 'mssql';

      // Check if this error indicates a lost connection
      if (isConnectionLostError(engineId, errorMessage)) {
        // …but only act on it if main agrees the connection is actually down.
        // For MSSQL this pattern set matches "is not active. Connect first.",
        // which main also returns for a connection that was never opened YET —
        // and a resume queries deferred connections while the background sweep
        // is opening them, so such a verdict routinely lands AFTER the connect
        // succeeded. Acting on it then marks a live pool dead.
        void confirmConnectionLost(connectionId).then((lost) => {
          if (!lost) return;
          console.log(`[DatabaseExplorer] Connection lost detected for ${connectionId}: ${errorMessage}`);
          markConnectionDisconnected(connectionId);
          updateConnectionStatus(connectionId, 'disconnected');
        });
      }
    },
    [activeConnections, markConnectionDisconnected, updateConnectionStatus]
  );

  const { handleToggleExpand } = useNodeExpansion({
    treeData,
    isConnectStale,
    listDatabasesFolder,
    fetchObjectsForFolder: fetchObjectsForFolderFenced,
    toggleNodeExpanded,
    updateNode,
    addDatabaseObjects,
    setLoadingObject,
    getPreloadedObjects,
    onFetchError: handleFetchError,
    queryClient,
  });

  const { handleRefresh } = useNodeRefresh({
    treeData,
    selectedNodeId,
    queryClient,
    fetchObjectsForFolder: fetchObjectsForFolderFenced,
    isConnectStale,
    invalidateDatabases,
    invalidateObjects,
    updateNode,
    addDatabaseObjects,
    mergePreloadedObjects,
    toggleNodeExpanded,
    handleToggleExpand,
    onRefreshRequest,
    onReconnect,
    listDatabasesFolder,
    // An explicit Refresh is the user asking for the current grouping — the
    // preload defers a layout change while they have the database open.
    restructureDatabaseChildren: (connectionId, database) => {
      const objects = useDatabaseStore.getState().preloadedData[connectionId]?.objects[database];
      // `[]` is an answer, not an absence: a grouped database whose wave found
      // nothing must still be able to flip back to flat on Refresh.
      if (!objects) return;
      restructureDatabaseChildren(
        connectionId,
        database,
        objects,
        useAppStore.getState().objectExplorerGrouping,
        { allowLayoutChange: true },
      );
    },
  });
  useEffect(() => { storageActionRefreshRef.current = handleRefresh; }, [handleRefresh]);

  // Terminal store for opening AI terminals from context menu
  const addTerminalSession = useTerminalStore((s) => s.addSession);

  // Connection info dialog
  const openConnectionInfoDialog = useConnectionInfoDialogStore((s) => s.openDialog);
  const handleConnectionInfo = useCallback((connectionId: string) => {
    const { connections } = useConnectionStore.getState();
    const connection = connections.find(c => c.id === connectionId);
    if (connection) {
      openConnectionInfoDialog(connection);
    }
  }, [openConnectionInfoDialog]);

  // Force reconnect — full rebuild of pools/auth state in the main process.
  // The escape hatch for "queries time out until I restart the app".
  const handleReconnect = useCallback((connectionId: string) => {
    const { connections, connectToDatabase } = useConnectionStore.getState();
    const connection = connections.find(c => c.id === connectionId);
    if (!connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }
    window.electronAPI?.analytics?.track('feature:connection_reconnect', { engine: connection.engine ?? 'unknown' }).catch(() => {});
    void connectToDatabase(connection, { force: true }).then((success) => {
      if (success) {
        toast.success({ title: 'Reconnected', description: safeConnectionName(connection), duration: 3000 });
      } else {
        const { connectionErrorById } = useConnectionStore.getState();
        toast.error({
          title: 'Reconnect failed',
          description: connectionErrorById[connectionId] || 'Failed to connect to database',
        });
      }
    });
  }, []);

  // Edit connection dialog (reuses NewConnectionPopup in edit mode)
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const updateConnectionInStore = useConnectionStore((state) => state.updateConnection);
  const deleteConnectionInStore = useConnectionStore((state) => state.deleteConnection);
  const handleEditConnection = useCallback((connectionId: string) => {
    const { connections } = useConnectionStore.getState();
    const connection = connections.find(c => c.id === connectionId);
    if (!connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }
    setEditingConnection(connection);
    window.electronAPI?.analytics?.track('feature:connection_edit_opened', { engine: connection.engine ?? 'unknown' }).catch(() => {});
  }, []);
  const handleEditConnectionSave = useCallback(async (connection: Connection, options: SaveConnectionOptions): Promise<boolean> => {
    const success = await updateConnectionInStore(connection, options);
    if (success) {
      setEditingConnection(null);
      window.electronAPI?.analytics?.track('feature:connection_edit_saved', { engine: connection.engine ?? 'unknown' }).catch(() => {});
    }
    return success;
  }, [updateConnectionInStore]);
  const handleEditConnectionDelete = useCallback(async (connectionId: string): Promise<boolean> => {
    const success = await deleteConnectionInStore(connectionId);
    if (success) {
      setEditingConnection(null);
      window.electronAPI?.analytics?.track('feature:connection_edit_deleted', {}).catch(() => {});
    }
    return success;
  }, [deleteConnectionInStore]);
  const handleEditConnectionCancel = useCallback(() => {
    setEditingConnection(null);
  }, []);

  // Actual AI-terminal creation for the Object Explorer "Open Terminal"
  // database action — extracted so it can be routed through the shared
  // consent gate below instead of being called directly (matches
  // `CreateAiTerminalFn` from `useAiWorkspaceSetupConsent`).
  const createAiTerminalForDatabase = useCallback(async (
    connection: ActiveConnection,
    database: string | undefined
  ) => {
    const connInfo = connection.connection;
    try {
      const result = await createTerminalSession({
        type: 'ai',
        connectionId: connection.id,
        database,
      });

      if (result.success && result.sessionId) {
        const session: TerminalSession = {
          id: result.sessionId,
          title: database
            ? `AI: ${safeConnectionName(connInfo)} - ${database}`
            : `AI: ${safeConnectionName(connInfo)}`,
          type: 'ai',
          connectionId: connection.id,
          database,
          workspacePath: result.workspacePath,
          createdAt: Date.now(),
          // Only seed an optimistic "Initializing…" state when the main
          // process actually scheduled a schema export for this session —
          // otherwise no `terminal:init-progress` event will ever arrive to
          // move it out of 'starting', leaving the context/status bars stuck
          // forever (e.g. mock engine, exportSchemaOnOpen off, unsupported
          // engine, no database). Leaving initState undefined shows the
          // normal ready state immediately instead.
          initState: result.schemaExportScheduled
            ? { phase: 'starting', message: 'Initializing AI workspace...' }
            : undefined,
        };
        addTerminalSession(session);
      } else {
        toast.error({
          title: 'Terminal',
          description: result.error || 'Failed to create terminal session.',
        });
      }
    } catch (err) {
      console.error('[DatabaseExplorer] Error creating AI terminal:', err);
      toast.error({
        title: 'Terminal',
        description: err instanceof Error ? err.message : 'Unexpected error creating terminal session.',
      });
    }
  }, [addTerminalSession]);

  // Shared AI-workspace consent gate ([D1→D2] parity, D4 fix) — the Object
  // Explorer "Open Terminal" database context-menu action used to call
  // createTerminalSession directly, bypassing the `confirmBeforeSetup`
  // dialog AiToolbarButton/TerminalPanel already gate behind. Renders
  // AiWorkspaceSetupConfirmDialog below (same hook + dialog TerminalPanel
  // uses) when the setting is on; creates immediately when it's off.
  const {
    consentContext,
    requestSetup: requestAiWorkspaceSetup,
    handleConfirm: handleConsentConfirm,
    handleCancel: handleConsentCancel,
  } = useAiWorkspaceSetupConsent(createAiTerminalForDatabase);

  // Handle opening AI terminal for a database context menu action
  const handleOpenTerminalForDatabase = useCallback((connectionId: string, database: string) => {
    const connection = activeConnections.find((c) => c.id === connectionId);
    if (!connection) {
      toast.error({ title: 'Terminal', description: 'Connection not found' });
      return;
    }
    requestAiWorkspaceSetup(connection, database);
  }, [activeConnections, requestAiWorkspaceSetup]);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  // Handle export .bacpac from context menu
  const handleExportBacpac = useCallback((connectionId: string, databaseName: string) => {
    setExportDialogState({ open: true, connectionId, databaseName });
  }, []);

  // Handle import .bacpac from context menu (connection level)
  const handleImportBacpac = useCallback((connectionId: string) => {
    setImportDialogState({ open: true, connectionId });
  }, []);

  // Handle backup .bak from context menu
  const handleBackupBak = useCallback((connectionId: string, databaseName: string) => {
    setBackupBakDialogState({ open: true, connectionId, databaseName });
  }, []);

  // Handle restore .bak from context menu
  const handleRestoreBak = useCallback((connectionId: string, databaseName: string) => {
    setRestoreBakDialogState({ open: true, connectionId, databaseName });
  }, []);

  // Handle PostgreSQL backup from context menu
  const handlePgsqlBackup = useCallback((connectionId: string, databaseName: string) => {
    setPgsqlBackupDialogState({ open: true, connectionId, databaseName });
  }, []);

  // Handle PostgreSQL restore from context menu
  const handlePgsqlRestore = useCallback((connectionId: string, databaseName: string) => {
    setPgsqlRestoreDialogState({ open: true, connectionId, databaseName });
  }, []);

  // Handle Clone Database from context menu
  const handleCloneDatabase = useCallback((connectionId: string, databaseName: string) => {
    setCloneDatabaseDialogState({ open: true, connectionId, databaseName });
  }, []);

  // Handle Clone Table from context menu
  const handleCloneTable = useCallback((connectionId: string, databaseName: string, schema: string, tableName: string) => {
    setCloneTableDialogState({ open: true, connectionId, databaseName, schema, tableName });
  }, []);

  // Handle Clone Table operation started - show progress dialog
  const handleCloneTableStarted = useCallback((params: {
    operationId: string;
    sourceTableName: string;
    targetTableName: string;
    targetConnectionId: string;
    targetDatabaseName: string;
  }) => {
    setCloneTableProgressState({
      open: true,
      operationId: params.operationId,
      sourceTableName: params.sourceTableName,
      targetTableName: params.targetTableName,
      targetConnectionId: params.targetConnectionId,
      targetDatabaseName: params.targetDatabaseName,
    });
  }, []);

  // A clone leaves a table the tree doesn't know about yet — including a clone
  // whose data copy failed, since the structure DDL has already committed by then.
  // Drop the cached table listing for the target database and reload whichever
  // Tables folders are currently materialized, so the table shows up on its own.
  const handleCloneTargetTableChanged = useCallback(() => {
    const { targetConnectionId, targetDatabaseName } = cloneTableProgressState;
    if (!targetConnectionId || !targetDatabaseName) return;

    invalidateObjects('table', targetConnectionId, targetDatabaseName);

    // Same `treeData` snapshot `handleRefresh` resolves node ids against, so a
    // node we find here is a node it can refresh.
    for (const nodeId of findTablesFolderNodeIds(treeData, targetConnectionId, targetDatabaseName)) {
      handleRefresh(nodeId);
    }
  }, [cloneTableProgressState, invalidateObjects, handleRefresh, treeData]);

  // Filter logic lives in `services/treeFilterService.ts` so it's unit-testable.
  // Current policy: pinned wrappers are EXCLUDED from search — every pinned
  // shortcut is a copy of a row that also lives under Databases, so including
  // both produced duplicate matches. See `treeFilterService.ts` (and spec
  // §"Object Explorer search") for the rationale.

  const handleContextMenuAction = useCallback(
    (nodeId: string, actionId: string) => {
      const rawNode = findNodeById(nodeId, treeData);
      if (!rawNode) return;

      // Review finding #1 + #15 — pinned-* wrappers carry the headline value
      // of the feature: right-click on a pinned table → "Select Top 1000",
      // "Script as CREATE", etc. The dispatcher below switches on raw
      // `node.type`, so unwrap pinned wrappers to a synthetic canonical node
      // BEFORE the switch. We construct just enough metadata for each
      // executor's `buildActionContext` to succeed (no `objectDetails.id` —
      // optional in downstream definition fetchers; falls back to name-based
      // lookup). Without this, every pinned-row action besides Refresh was a
      // silent no-op, AND the table/view/routine executors would have crashed
      // on `metadata.objectDetails` destructuring if they were ever reached.
      const unwrapped = unwrapPinnedForAction(rawNode as unknown as DatabaseExplorerNode);
      const node = (unwrapped ?? rawNode) as unknown as DatabaseExplorerNode;

      // T12 — the table/view context menu's "AI" submenu (`ai:<taskId>`,
      // namespaced by `aiSubmenuBuilder.ts` so it can't collide with any other
      // action id — T12 fix round 1, M-6). Intercepted here (not in the
      // per-type executors below) because launching needs the
      // `useAiTaskLauncher` hook, which only a component can call.
      const aiTaskId =
        node.type === 'table' || node.type === 'view' ? parseAiTaskActionId(actionId) : null;
      if (aiTaskId) {
        const ctx =
          node.type === 'table'
            ? buildTableAiTaskContext(node as DatabaseObjectTreeNode)
            : buildViewAiTaskContext(node as DatabaseObjectTreeNode);
        if (ctx) {
          launchTask(aiTaskId, ctx, 'object-explorer');
        }
        return;
      }

      if (node.type === 'table') {
        executeTableContextMenuAction(
          actionId,
          nodeId,
          { onRefresh: () => handleRefresh(nodeId), onCloneTable: handleCloneTable },
          node as DatabaseObjectTreeNode
        );
      } else if (node.type === 'column') {
        executeColumnContextMenuAction(actionId, nodeId, node);
      } else if (node.type === 'view') {
        executeViewContextMenuAction(
          actionId,
          nodeId,
          { onRefresh: () => handleRefresh(nodeId) },
          node as DatabaseObjectTreeNode
        );
      } else if (node.type === 'stored-procedure' || node.type === 'function') {
        executeRoutineContextMenuAction(
          actionId,
          nodeId,
          { onRefresh: () => handleRefresh(nodeId) },
          node as DatabaseObjectTreeNode
        );
      } else if (node.type === 'key') {
        const keyMeta = node.metadata as KeyNodeMetadata | undefined;
        if (keyMeta) {
          executeKeyContextMenuAction(actionId, keyMeta);
        }
      } else if (node.type === 'constraint') {
        const constraintMeta = node.metadata as ConstraintNodeMetadata | undefined;
        if (constraintMeta) {
          executeConstraintContextMenuAction(actionId, constraintMeta);
        }
      } else if (node.type === 'index') {
        const indexMeta = node.metadata as IndexNodeMetadata | undefined;
        if (indexMeta) {
          executeIndexContextMenuAction(actionId, indexMeta);
        }
      } else if (node.type === 'trigger') {
        const triggerMeta = node.metadata as TriggerNodeMetadata | undefined;
        if (triggerMeta) {
          executeTriggerContextMenuAction(actionId, triggerMeta);
        }
      } else if (node.type === 'statistic') {
        const statisticMeta = node.metadata as StatisticNodeMetadata | undefined;
        if (statisticMeta) {
          executeStatisticContextMenuAction(actionId, statisticMeta);
        }
      } else if (isSecurityObjectType(node.type)) {
        const metadata = node.metadata as SecurityObjectNodeMetadata | undefined;
        if (!metadata?.connectionId || !metadata?.principalName || !metadata?.engine) return;

        const context = {
          nodeType: node.type,
          connectionId: metadata.connectionId,
          database: metadata.database,
          principalId: metadata.principalId,
          principalName: metadata.principalName,
          engine: metadata.engine,
          isSystem: metadata.isSystem,
        } as const;

        executeSecurityContextMenuAction(actionId, nodeId, {
          onRefresh: () => handleRefresh(nodeId),
          onProperties: () => openSecurityPropertiesFromExplorerNode(context),
          onScriptCreate: () => void openSecurityScriptAsFromExplorerNode(context, 'create'),
          onScriptAlter: () => void openSecurityScriptAsFromExplorerNode(context, 'alter'),
          onScriptDrop: () => void openSecurityScriptAsFromExplorerNode(context, 'drop'),
        });
      } else if (isAgentNodeType(node.type)) {
        executeAgentContextMenuAction(actionId, node, { onRefresh: () => handleRefresh(nodeId) });
      } else if (node.type === 'directory') {
        executeDirectoryContextMenuAction(
          actionId,
          nodeId,
          { onRefresh: () => handleRefresh(nodeId) },
          node as DatabaseObjectTreeNode
        );
      } else if (isUserTypeObjectType(node.type)) {
        executeUserTypeContextMenuAction(
          actionId,
          nodeId,
          { onRefresh: () => handleRefresh(nodeId) },
          node as DatabaseObjectTreeNode
        );
      } else if (['package', 'sequence', 'synonym', 'db-link', 'materialized-view', 'type', 'property-graph'].includes(node.type)) {
        executeOracleObjectContextMenuAction(
          actionId,
          nodeId,
          { onRefresh: () => handleRefresh(nodeId) },
          node as DatabaseObjectTreeNode
        );
      } else if (node.type === 'partition' || node.type === 'partitions-folder') {
        // Must precede the generic `-folder` branch since 'partitions-folder'
        // also ends with `-folder`.
        executePartitionContextMenuAction(
          actionId,
          nodeId,
          { onRefresh: () => handleRefresh(nodeId) },
          node as PartitionTreeNode | PartitionsFolderNode
        );
      } else if (node.type === 'connection') {
        // Handle connection-level actions (Connection Info, Import .bacpac, Disconnect, Refresh)
        const connNode = node as ConnectionTreeNode;
        executeConnectionContextMenuAction(
          actionId,
          nodeId,
          {
            onRefresh: () => handleRefresh(nodeId),
            onReconnect: handleReconnect,
            onDisconnect: onDisconnect,
            onImportBacpac: handleImportBacpac,
            onConnectionInfo: handleConnectionInfo,
            onEditConnection: handleEditConnection,
          },
          connNode
        );
      } else if (node.type === 'dashboard') {
        // S23 — Open / Rename / Duplicate / Delete; S13 adds Present. Rename
        // opens the shared settings dialog (also used by the workspace tab's
        // own kebab menu); Duplicate/Delete/Present are self-contained
        // (confirm/open + mutate + toast + analytics where applicable) in
        // dashboardExplorerActions.ts.
        const dashboardId = (node.metadata as { dashboardId?: string } | undefined)?.dashboardId;
        if (!dashboardId) return;

        if (actionId === 'open') {
          openDashboardFromExplorer(dashboardId);
        } else if (actionId === 'present') {
          presentDashboardFromExplorer(dashboardId);
        } else if (actionId === 'rename') {
          const target = useDashboardStore.getState().dashboards.find((d) => d.id === dashboardId);
          if (target) setDashboardRenameTarget(target);
        } else if (actionId === 'duplicate') {
          void duplicateDashboardFromExplorer(dashboardId);
        } else if (actionId === 'export-definition') {
          void exportDashboardDefinitionFromExplorer(dashboardId);
        } else if (actionId === 'delete') {
          void deleteDashboardFromExplorer(dashboardId);
        }
      } else if (isStorageNodeType(node.type) || node.type === 'pinned-storage') {
        // Azure Storage (spec §5.2): Open / Copy URL / Pin / Refresh / portal.
        executeStorageContextMenuAction(actionId, node as StorageMenuNode, { onRefresh: (id) => handleRefresh(id), track });
      } else if (node.type === 'pinned-workspace') {
        // Task 16 — Open / Rename… / Update from open tab / Unpin.
        const wsNode = node as unknown as PinnedWorkspaceNode;
        const wsConnectionId = wsNode.metadata?.connectionId;
        if (wsConnectionId) {
          executePinnedWorkspaceContextMenuAction(
            actionId,
            { connectionId: wsConnectionId, ref: wsNode.pinRef },
            {
              track,
              onRename: () =>
                setRenamePinDialogState({
                  open: true,
                  connectionId: wsConnectionId,
                  ref: wsNode.pinRef,
                }),
            },
          );
        }
      } else if (node.type === 'database') {
        // Handle database-level "Script New..." actions
        const dbNode = node as DatabaseTreeNode;
        const connectionId = dbNode.metadata?.connectionId;
        const databaseName = dbNode.name;

        if (connectionId) {
          const { connections } = useConnectionStore.getState();
          const connection = connections.find(c => c.id === connectionId);
          const engine = connection?.engine || 'mssql';

          switch (actionId) {
            case 'script-new-table': {
              const context = createContextFromDatabaseNode(connectionId, databaseName, engine);
              handleScriptNewTable(context);
              return;
            }
            case 'script-new-view': {
              const context = createContextFromDatabaseNode(connectionId, databaseName, engine);
              handleScriptNewView(context);
              return;
            }
            case 'script-new-procedure': {
              const context = createContextFromDatabaseNode(connectionId, databaseName, engine);
              handleScriptNewProcedure(context);
              return;
            }
            case 'script-new-function': {
              const context = createContextFromDatabaseNode(connectionId, databaseName, engine);
              handleScriptNewFunction(context);
              return;
            }
          }
        }

        // Handle other database actions
        executeDatabaseContextMenuAction(
          actionId,
          nodeId,
          {
            onRefresh: () => handleRefresh(nodeId),
            onOpenTerminal: handleOpenTerminalForDatabase,
            onExportBacpac: handleExportBacpac,
            onBackupBak: handleBackupBak,
            onRestoreBak: handleRestoreBak,
            onPgsqlBackup: handlePgsqlBackup,
            onPgsqlRestore: handlePgsqlRestore,
            onCloneDatabase: handleCloneDatabase,
          },
          dbNode
        );
      } else if (node.type === 'schema-container-folder') {
        // Schema-scoped "Script New" + "Script Schema As" actions. The schema
        // comes from the NODE metadata (not the engine default), so templates
        // target <schema>.<name>.
        const schemaNode = node as DatabaseExplorerNode;
        const meta = schemaNode.metadata as
          | { connectionId?: string; database?: string; schema?: string }
          | undefined;
        const connectionId = meta?.connectionId;
        const database = meta?.database ?? '';
        const schema = meta?.schema ?? schemaNode.name;

        if (connectionId) {
          switch (actionId) {
            case 'script-new-table':
              handleScriptNewTable({ connectionId, database, schema });
              window.electronAPI?.analytics?.track('feature:script_schema_new_table', { source: 'schema_context_menu' }).catch(() => {});
              return;
            case 'script-new-view':
              handleScriptNewView({ connectionId, database, schema });
              window.electronAPI?.analytics?.track('feature:script_schema_new_view', { source: 'schema_context_menu' }).catch(() => {});
              return;
            case 'script-new-procedure':
              handleScriptNewProcedure({ connectionId, database, schema });
              window.electronAPI?.analytics?.track('feature:script_schema_new_procedure', { source: 'schema_context_menu' }).catch(() => {});
              return;
            case 'script-new-function':
              handleScriptNewFunction({ connectionId, database, schema });
              window.electronAPI?.analytics?.track('feature:script_schema_new_function', { source: 'schema_context_menu' }).catch(() => {});
              return;
            case 'script-schema-create':
              handleScriptSchemaAs(schemaNode, 'create');
              return;
            case 'script-schema-drop':
              handleScriptSchemaAs(schemaNode, 'drop');
              return;
            case 'script-schema-all-objects':
              handleScriptAllObjects(schemaNode);
              return;
          }
        }

        if (actionId === 'refresh') {
          handleRefresh(nodeId);
        }
      } else if (node.type.endsWith('-folder')) {
        // Handle folder "Script New..." actions
        const folderNode = node as FolderTreeNode;
        const connectionId = folderNode.metadata?.connectionId;
        const database = folderNode.metadata?.database;

        if (connectionId) {
          const { connections } = useConnectionStore.getState();
          const connection = connections.find(c => c.id === connectionId);
          const engine = connection?.engine || 'mssql';

          switch (actionId) {
            case 'open-security-manager': {
              if (node.type === 'database-security-folder') {
                openSecurityManagerFromExplorerFolder({ connectionId, view: 'database', database });
                return;
              }

              if (node.type === 'server-security-folder' || node.type === 'logins-folder') {
                const objectType = engine === 'oracle' ? 'oracle-user' : engine === 'mysql' ? 'mysql-user' : 'login';
                openSecurityManagerFromExplorerFolder({ connectionId, view: 'server', objectType });
                return;
              }

              if (node.type === 'server-roles-folder') {
                const objectType = engine === 'oracle' ? 'oracle-role' : 'server-role';
                openSecurityManagerFromExplorerFolder({ connectionId, view: 'server', objectType });
                return;
              }

              if (node.type === 'pg-roles-folder' || node.type === 'login-roles-folder') {
                openSecurityManagerFromExplorerFolder({ connectionId, view: 'server', objectType: 'login-role' });
                return;
              }

              if (node.type === 'group-roles-folder') {
                openSecurityManagerFromExplorerFolder({ connectionId, view: 'server', objectType: 'group-role' });
                return;
              }
              break;
            }
            case 'script-new-database':
              handleScriptNewDatabase(connectionId);
              return;
            case 'restore-bak':
              if (node.type === 'databases-folder') {
                if (engine === 'mssql' || engine === 'mock') {
                  handleRestoreBak(connectionId, '');
                } else {
                  toast.error({ title: 'Restore', description: 'Restore (.bak) is only available for SQL Server connections.' });
                }
                return;
              }
              break;
            case 'script-new-table': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewTable(context);
              return;
            }
            case 'script-new-view': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewView(context);
              return;
            }
            case 'script-new-procedure': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewProcedure(context);
              return;
            }
            case 'script-new-function': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewFunction(context);
              return;
            }
            case 'script-new-package': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewPackage(context);
              return;
            }
            case 'script-new-sequence': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewSequence(context);
              return;
            }
            case 'script-new-synonym': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewSynonym(context);
              return;
            }
            case 'script-new-dblink': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewDbLink(context);
              return;
            }
            case 'script-new-trigger': {
              const context = createContextFromFolderMetadata(connectionId, database, engine);
              handleScriptNewTrigger(context);
              return;
            }
            case 'script-new-login':
              openSecurityCreateFromExplorerFolder({ connectionId, view: 'server', objectType: 'login' });
              return;
            case 'script-new-server-role':
              openSecurityCreateFromExplorerFolder({ connectionId, view: 'server', objectType: 'server-role' });
              return;
            case 'script-new-login-role':
              openSecurityCreateFromExplorerFolder({ connectionId, view: 'server', objectType: 'login-role' });
              return;
            case 'script-new-group-role':
              openSecurityCreateFromExplorerFolder({ connectionId, view: 'server', objectType: 'group-role' });
              return;
            case 'script-new-user':
              openSecurityCreateFromExplorerFolder({ connectionId, view: 'database', objectType: 'user', database });
              return;
            case 'script-new-database-role':
              openSecurityCreateFromExplorerFolder({
                connectionId,
                view: 'database',
                objectType: 'database-role',
                database,
              });
              return;
            case 'script-new-schema':
              openSecurityCreateFromExplorerFolder({ connectionId, view: 'database', objectType: 'schema', database });
              return;
            case 'tables-overview':
            case 'views-overview':
            case 'procedures-overview':
            case 'functions-overview':
            case 'package-explorer':
              executeFolderContextMenuAction(
                actionId,
                nodeId,
                { onRefresh: () => handleRefresh(nodeId) },
                folderNode
              );
              return;
          }
        }

        // Handle refresh action for folders
        if (actionId === 'refresh') {
          handleRefresh(nodeId);
        }
      } else if (actionId === 'refresh') {
        handleRefresh(nodeId);
      }
    },
    [handleRefresh, handleExportBacpac, handleImportBacpac, handleBackupBak, handleRestoreBak, handlePgsqlBackup, handlePgsqlRestore, handleCloneDatabase, handleCloneTable, handleOpenTerminalForDatabase, onDisconnect, handleReconnect, handleConnectionInfo, handleEditConnection, treeData, track, launchTask],
  );

  const renderCustomActions = useCallback(() => {
    if (onDisconnect && selectedNodeId) {
      const node = findNodeById(selectedNodeId, treeData);
      if (!node) return null;

      // Find the connection ID - either from the node itself or its metadata
      let connectionId: string | undefined;
      if (node.type === 'connection') {
        connectionId = (node as ConnectionTreeNode).metadata.id;
      } else {
        // For child nodes, get connectionId from metadata
        connectionId = (node.metadata as { connectionId?: string })?.connectionId;
      }

      if (connectionId) {
        return (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Disconnect"
            onClick={() => onDisconnect(connectionId)}
            data-testid={`explorer-disconnect-button-${connectionId}`}
          >
            <PowerOff className="h-4 w-4" />
          </Button>
        );
      }
    }

    return null;
  }, [onDisconnect, selectedNodeId, treeData]);

  const processedTreeData = useMemo(() => {
    const processed = processDatabaseTree(treeData) as DatabaseExplorerNode[];
    return filterTreeNodes(processed, searchTerm) as DatabaseExplorerNode[];
  }, [treeData, searchTerm]);

  return (
    <>
      <DatabaseExplorerTree
        nodes={processedTreeData}
        onNodeClick={handleNodeClick}
        onToggleExpand={handleToggleExpand}
        onRefresh={handleRefresh}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onAddConnection={onAddConnection}
        onCollapseAll={collapseAll}
        onCollapseSidebar={collapseSidebar}
        renderCustomActions={renderCustomActions}
        className={className}
        selectedNodeId={selectedNodeId}
        onContextMenuAction={handleContextMenuAction}
        highlightedConnectionIds={highlightedConnectionIds}
        highlightedNodeId={highlightedNodeId}
        onNodeHighlightComplete={handleHighlightComplete}
        onHighlightedNodeComplete={clearHighlightedNodeId}
        onReconnect={onReconnect}
        aiCliAvailable={aiCliAvailable}
        aiCliDetectionLoading={aiCliDetectionLoading}
      />

      {/* AI workspace setup consent dialog (D4 fix — parity with TerminalPanel
          / AiToolbarButton). Seeded with the database the user right-clicked
          in the Object Explorer as the initial target. */}
      {consentContext && (
        <AiWorkspaceSetupConfirmDialog
          open
          connection={consentContext.connection}
          initialDatabase={consentContext.database}
          onConfirm={handleConsentConfirm}
          onCancel={handleConsentCancel}
        />
      )}

      {/* T12 — raised by `useAiTaskLauncher().launchTask` when the table/view
          "AI" submenu is used with no Claude Code or Codex CLI installed. */}
      <AiChatCliRequiredDialog open={cliRequiredOpen} onOpenChange={setCliRequiredOpen} />

      {/* Export .bacpac dialog */}
      <ExportDialog
        open={exportDialogState.open}
        onOpenChange={(open) =>
          setExportDialogState((prev) => ({ ...prev, open }))
        }
        connectionId={exportDialogState.connectionId}
        databaseName={exportDialogState.databaseName}
      />

      {/* Import .bacpac dialog */}
      <ImportDialog
        open={importDialogState.open}
        onOpenChange={(open) =>
          setImportDialogState((prev) => ({ ...prev, open }))
        }
        connectionId={importDialogState.connectionId}
      />

      {/* Backup .bak dialog */}
      <BackupDatabaseDialog
        open={backupBakDialogState.open}
        onOpenChange={(open) =>
          setBackupBakDialogState((prev) => ({ ...prev, open }))
        }
        connectionId={backupBakDialogState.connectionId}
        databaseName={backupBakDialogState.databaseName}
      />

      {/* Restore .bak dialog */}
      <RestoreDatabaseDialog
        open={restoreBakDialogState.open}
        onOpenChange={(open) =>
          setRestoreBakDialogState((prev) => ({ ...prev, open }))
        }
        connectionId={restoreBakDialogState.connectionId}
        databaseName={restoreBakDialogState.databaseName}
        onRefreshDatabases={(connectionId) => {
          const databasesFolderNodeId = createDatabasesFolderNodeId(connectionId);
          handleRefresh(databasesFolderNodeId);
        }}
      />

      {/* PostgreSQL backup dialog */}
      <PostgresBackupDialog
        open={pgsqlBackupDialogState.open}
        onOpenChange={(open) =>
          setPgsqlBackupDialogState((prev) => ({ ...prev, open }))
        }
        connectionId={pgsqlBackupDialogState.connectionId}
        databaseName={pgsqlBackupDialogState.databaseName}
      />

      {/* PostgreSQL restore dialog */}
      <PostgresRestoreDialog
        open={pgsqlRestoreDialogState.open}
        onOpenChange={(open) =>
          setPgsqlRestoreDialogState((prev) => ({ ...prev, open }))
        }
        connectionId={pgsqlRestoreDialogState.connectionId}
        databaseName={pgsqlRestoreDialogState.databaseName}
      />

      {/* Clone database dialog */}
      <CloneDatabaseDialog
        open={cloneDatabaseDialogState.open}
        onOpenChange={(open) =>
          setCloneDatabaseDialogState((prev) => ({ ...prev, open }))
        }
        connectionId={cloneDatabaseDialogState.connectionId}
        databaseName={cloneDatabaseDialogState.databaseName}
        onRefreshDatabases={(connectionId) => {
          const databasesFolderNodeId = createDatabasesFolderNodeId(connectionId);
          handleRefresh(databasesFolderNodeId);
        }}
      />

      {/* Clone table dialog */}
      <CloneTableDialog
        open={cloneTableDialogState.open}
        onOpenChange={(open) =>
          setCloneTableDialogState((prev) => ({ ...prev, open }))
        }
        connectionId={cloneTableDialogState.connectionId}
        databaseName={cloneTableDialogState.databaseName}
        schema={cloneTableDialogState.schema}
        tableName={cloneTableDialogState.tableName}
        onCloneStarted={handleCloneTableStarted}
      />

      {/* Clone table progress dialog */}
      <CloneTableProgressDialog
        open={cloneTableProgressState.open}
        onClose={() =>
          setCloneTableProgressState((prev) => ({ ...prev, open: false }))
        }
        operationId={cloneTableProgressState.operationId}
        sourceTableName={cloneTableProgressState.sourceTableName}
        targetTableName={cloneTableProgressState.targetTableName}
        onTargetTableChanged={handleCloneTargetTableChanged}
      />

      {/* Rename pin dialog (Task 16). On rejection (size cap, concurrent
          remote unpin) the dialog stays OPEN with the user's input — the
          service toasts the reason; the close only runs via onSuccess. */}
      <RenamePinDialog
        open={renamePinDialogState.open}
        initialName={renamePinDialogState.ref?.name ?? ''}
        onCancel={() => setRenamePinDialogState((prev) => ({ ...prev, open: false }))}
        onSave={(name) => {
          const { connectionId, ref } = renamePinDialogState;
          if (!ref) {
            setRenamePinDialogState((prev) => ({ ...prev, open: false }));
            return;
          }
          void renameWorkspacePin({
            connectionId,
            ref,
            name,
            track,
            onSuccess: () => setRenamePinDialogState((prev) => ({ ...prev, open: false })),
          });
        }}
      />

      {/* Connection info dialog */}
      <ConnectionInfoDialog />

      {/* Edit connection dialog (reuses NewConnectionPopup in edit mode) */}
      {editingConnection && (
        <NewConnectionPopup
          onSave={handleEditConnectionSave}
          onCancel={handleEditConnectionCancel}
          onDelete={handleEditConnectionDelete}
          editConnection={editingConnection}
          isConnected={activeConnections.some((c) => c.id === editingConnection.id)}
        />
      )}

      {/* Oracle object management dialogs */}
      <CreateSequenceDialog />
      <CreateSynonymDialog />
      <CreateDbLinkDialog />

      {/* Explorer confirm dialog (delete actions from context menus) */}
      <ExplorerConfirmDialog />

      {/* S23 — "Rename" on a Dashboards-node child, via the shared settings dialog */}
      {dashboardRenameTarget && (
        <DashboardSettingsDialog
          dashboard={dashboardRenameTarget}
          dashboards={dashboards}
          open={!!dashboardRenameTarget}
          onOpenChange={(open) => {
            if (!open) setDashboardRenameTarget(null);
          }}
          onRename={(name) => {
            void renameDashboardFromExplorer(dashboardRenameTarget.id, name);
          }}
          onSaveParameters={(parameters) => {
            void saveDashboardParametersFromExplorer(dashboardRenameTarget.id, parameters);
          }}
          onSavePlaylist={(playlist) => {
            void saveDashboardPlaylistFromExplorer(dashboardRenameTarget.id, playlist);
          }}
        />
      )}

      {/* Routine overload picker (Slice 9: F12 on PG/Oracle overloaded routine) */}
      <RoutinePickerDialog />

      {/* Rename preview dialog (Slice 27: programmatic open via useRenamePreviewStore) */}
      <RenamePreviewDialog />
    </>
  );
}
